import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import { formatDate } from '../utils/helpers';
import { getMosqueUrl, getSlugFromParams, parseSocietyUrl } from '../utils/urlHelpers';
import { userCanManageMosque } from '../utils/mosqueAccess';
import mosqueService from '../services/mosqueService';
import GoogleMapsLocationLink from '../components/common/GoogleMapsLocationLink';
import ListingCoverHero from '../components/common/ListingCoverHero';
import { getListingCoverImageUrl } from '../utils/imageFallback';
import ClickableImageGrid from '../components/common/ClickableImageGrid';
import useImageLightbox from '../hooks/useImageLightbox';
import { normalizeGalleryImages } from '../utils/imageLightbox';
import MosquePrayerTimingsBoard from '../components/mosques/MosquePrayerTimingsBoard';
import MosqueFeaturesSection from '../components/mosques/MosqueFeaturesSection';
import { mosqueStreetAddressFromStored } from '../utils/mosqueFeatures';

const MosqueDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const mosqueSegment = getSlugFromParams(params, 'mosque');
  const { isSocietySpecific, societySlug: pathSocietySlug } = parseSocietyUrl(location.pathname);
  const [mosque, setMosque] = useState(null);
  const [relatedMosques, setRelatedMosques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const { openLightbox, Lightbox } = useImageLightbox();

  useEffect(() => {
    if (!mosqueSegment) {
      setLoading(false);
      return undefined;
    }

    const loadMosque = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await mosqueService.getMosque(mosqueSegment);
        setMosque(data);
        setViews(data.views || 0);
      } catch (err) {
        console.error('Error loading mosque:', err);
        setError('Failed to load mosque details');
        setMosque(null);
      } finally {
        setLoading(false);
      }
    };

    loadMosque();
  }, [mosqueSegment]);

  useEffect(() => {
    if (!mosque?.slug) return;
    if (String(mosqueSegment) === String(mosque.slug)) return;
    if (!/^\d+$/.test(String(mosqueSegment))) return;
    navigate(getMosqueUrl(mosque, isSocietySpecific ? pathSocietySlug : null), { replace: true });
  }, [mosque, mosqueSegment, navigate, isSocietySpecific, pathSocietySlug]);

  useEffect(() => {
    if (!mosque?.id) return undefined;

    let cancelled = false;

    const loadRelated = async () => {
      try {
        const societyId = mosque?.society?.id;
        const data = await mosqueService.getRelatedMosques(mosque.id, {
          society: societyId,
        });
        if (!cancelled) {
          setRelatedMosques(data.results || data || []);
        }
      } catch (err) {
        console.error('Error loading related mosques:', err);
      }
    };

    loadRelated();
    return () => {
      cancelled = true;
    };
  }, [mosque?.id, mosque?.society?.id]);

  const handleDelete = async () => {
    if (!isAuthenticated || !user || !mosque || !userCanManageMosque(user, mosque)) {
      return;
    }

    if (window.confirm('Are you sure you want to delete this mosque?')) {
      try {
        const key = mosque.slug || mosque.id;
        await mosqueService.deleteMosque(key);
        navigate('/mosques');
      } catch (err) {
        console.error('Error deleting mosque:', err);
        alert('Failed to delete mosque');
      }
    }
  };

  const handleEdit = () => {
    if (!isAuthenticated || !user || !mosque || !userCanManageMosque(user, mosque)) {
      return;
    }
    navigate(`${getMosqueUrl(mosque, isSocietySpecific ? pathSocietySlug : null)}/edit`);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSendingMessage(true);
    try {
      console.log('Sending message:', message);
      setMessage('');
      setShowMessageForm(false);
      alert('Message sent successfully!');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading mosque details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !mosque) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Mosque Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The mosque you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/mosques')}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              Back to Mosques
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const canManage = isAuthenticated && user && userCanManageMosque(user, mosque);
  const mosqueGallerySlides = normalizeGalleryImages(mosque.images, mosque.name);

  return (
    <PageLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
                <ListingCoverHero
                  src={getListingCoverImageUrl(mosque)}
                  alt={mosque.name}
                  onClick={
                    mosqueGallerySlides.length > 0
                      ? () => openLightbox(mosqueGallerySlides, 0)
                      : undefined
                  }
                />
                <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h1 className="text-3xl font-bold text-gray-900">{mosque.name}</h1>
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        Mosque
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{mosque.society?.name || 'Unknown Society'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>{views} views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Posted {formatDate(mosque.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mb-4">
                      {mosque.phone && (
                        <div className="flex items-center gap-2 text-blue-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <a href={`tel:${mosque.phone}`} className="hover:underline">
                            {mosque.phone}
                          </a>
                        </div>
                      )}
                      {mosque.address && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{mosqueStreetAddressFromStored(mosque.address)}</span>
                        </div>
                      )}
                    </div>

                    <GoogleMapsLocationLink
                      className="mb-4"
                      variant="card"
                      heading="Location"
                      latitude={mosque.latitude}
                      longitude={mosque.longitude}
                      address={mosqueStreetAddressFromStored(mosque.address)}
                      society={mosque.society}
                      name={mosque.name}
                    />

                    {(mosque.imam_name || mosque.imam_phone) && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <h3 className="font-semibold text-green-800 mb-2">Imam Information</h3>
                        <div className="space-y-1">
                          {mosque.imam_name && (
                            <p className="text-green-700">
                              <span className="font-medium">Name:</span> {mosque.imam_name}
                            </p>
                          )}
                          {mosque.imam_phone && (
                            <p className="text-green-700">
                              <span className="font-medium">Phone:</span>
                              <a href={`tel:${mosque.imam_phone}`} className="ml-1 hover:underline">
                                {mosque.imam_phone}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <div className="flex gap-2 mt-4 sm:mt-0">
                      <button
                        type="button"
                        onClick={handleEdit}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                </div>
              </div>

              <ClickableImageGrid
                images={mosque.images}
                title="Images"
                namePrefix={mosque.name}
              />
              {Lightbox}

              <MosqueFeaturesSection mosque={mosque} />

              <MosquePrayerTimingsBoard
                mosque={mosque}
                board={mosque.prayer_timings_board}
                canEdit={canManage}
                onSaved={(board) => setMosque((prev) => ({ ...prev, prayer_timings_board: board }))}
              />

              {!canManage && isAuthenticated && (
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Mosque</h2>
                  {!showMessageForm ? (
                    <button
                      type="button"
                      onClick={() => setShowMessageForm(true)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Send Message
                    </button>
                  ) : (
                    <form onSubmit={handleSendMessage} className="space-y-4">
                      <div>
                        <label htmlFor="mosque-msg" className="block text-sm font-medium text-gray-700 mb-2">
                          Message
                        </label>
                        <textarea
                          id="mosque-msg"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Type your message here..."
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={sendingMessage}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                        >
                          {sendingMessage ? 'Sending...' : 'Send Message'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowMessageForm(false)}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {relatedMosques.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Mosques</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedMosques.map((relatedMosque) => (
                      <div
                        key={relatedMosque.id}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          navigate(
                            getMosqueUrl(relatedMosque, isSocietySpecific ? pathSocietySlug : null),
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            navigate(
                              getMosqueUrl(relatedMosque, isSocietySpecific ? pathSocietySlug : null),
                            );
                          }
                        }}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      >
                        <h3 className="font-semibold text-gray-900 mb-2">{relatedMosque.name}</h3>
                        <p className="text-sm text-gray-500">{relatedMosque.society?.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MosqueDetailPage;
